import { Badge, Box, HStack, Text } from "@chakra-ui/layout";
import {
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import {
  ProjectOrderByColumnEnum,
  type ProjectTemplateFragment,
} from "@src/__generated__/graphql";
import {
  ACTIONS_COLUMN_ID,
  type BodyColumnDef,
  ButtonLink,
  DataTable,
  LoadingOverlay,
  MenuItemLink,
  ModalConfirm,
  ModuleTitle,
} from "@src/components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";
import { Pagination } from "@src/components/ui-kit/table/pagination";
import { ProjectCell } from "@src/components/widgets/TableCell";
import { FiltersPanel } from "@src/utils/components/filters/components/filters-panel";
import { currency } from "@src/utils/formatters";
import { useStore, useUpdateURI } from "@src/utils/hooks";
import { observer, useLocalObservable } from "mobx-react-lite";
import { useMemo } from "react";
import Column from "../../projects/list/Table/column";
import type { TemplatesNavigationProps } from "../navigation";
import { ProjectTemplatesStore } from "./store";

type ProjectTemplatesPageProps = {
  template: TemplatesNavigationProps["currentTemplate"];
};
export const ProjectTemplatesPage = observer(function ProjectTemplatesPage({
  template,
}: ProjectTemplatesPageProps) {
  const appStore = useStore();
  const { store } = useLocalObservable(() => ({
    store: new ProjectTemplatesStore(appStore),
  }));

  useUpdateURI({
    search: store.searchTerm,
    where: store.where.asURLSearchParam.where,
    orderBy: store.orderBy.asURLSearchParam.orderBy,
    first: store.pagination.asURLSearchParam.first,
    page: store.pagination.asURLSearchParam.page,
  });

  const columns = useMemo<
    BodyColumnDef<ProjectTemplateFragment, ProjectOrderByColumnEnum>[]
  >(
    () => [
      {
        id: "title",
        meta: { label: t`Template name` },
        header: t`Template name`,
        orderBy: ProjectOrderByColumnEnum.ProjectCode,
        cell: ({ row }) => (
          <ProjectCell.Title
            project={row.original}
            brand={row.original.brand}
          />
        ),
      },
      {
        id: "total-budget",
        meta: { label: t`Total budget` },
        header: t`Total budget`,
        orderBy: ProjectOrderByColumnEnum.TotalBudget,
        cell: ({ row }) =>
          currency.formatByWorkspace(
            row.original.statsCached?.total_budget ?? 0,
          ),
      },
      {
        id: "work",
        meta: { label: t`Work` },
        header: t`Work`,
        cell: ({ row }) =>
          currency.formatByWorkspace(
            row.original.statsCached?.ourWork.total_budget ?? 0,
          ),
      },
      {
        id: "expenses",
        meta: { label: t`Expenses` },
        header: t`Expenses`,
        cell: ({ row }) =>
          currency.formatByWorkspace(
            row.original.statsCached?.expenses.planned_expenses ?? 0,
          ),
      },
      {
        id: "categories",
        meta: { label: t`Categories` },
        header: t`Categories`,
        cell: ({ row }) => (
          <HStack wrap="wrap" mt="1">
            {row.original.categories?.map(({ id, name }) => (
              <Badge
                key={`${id}-${name}`}
                fontSize="2xs"
                fontWeight="thin"
                bg="grey.50"
                size="xs"
              >
                {name}
              </Badge>
            ))}
          </HStack>
        ),
      },
      {
        id: "note",
        meta: { label: t`Note` },
        header: t`Note`,
        cell: ({ row }) => (
          <Column.Note
            id={row.original.id}
            note={row.original.note ?? undefined}
          />
        ),
      },
      {
        id: ACTIONS_COLUMN_ID,
        meta: { label: t`Actions` },
        header: t`Actions`,
        cell: ({ row }) => (
          <HStack>
            <ButtonLink
              href={{
                pathname: "/projects/create",
                query: {
                  templateId: row.original.id,
                },
              }}
              leftIcon={<Icon name="plus" />}
              size="sm"
            >
              <Trans>Project</Trans>
            </ButtonLink>
            <Menu>
              <MenuButton
                as={IconButton}
                colorScheme="grey"
                icon={<Icon name="dots-vertical" />}
                variant="ghost"
              />
              <MenuList>
                <MenuItemLink
                  href={{
                    pathname: "/projects/edit",
                    query: {
                      id: row.original.id,
                    },
                  }}
                >
                  <Trans>Edit</Trans>
                </MenuItemLink>
                <MenuItem
                  onClick={() => {
                    store.handleDeleteModalOpen(row.original.id);
                  }}
                >
                  <Trans>Delete</Trans>
                </MenuItem>
              </MenuList>
            </Menu>
          </HStack>
        ),
      },
    ],
    [store.handleDeleteModalOpen],
  );

  return (
    <Box>
      <HStack justifyContent="space-between" mb="4">
        <ModuleTitle>
          <Text fontWeight="inherit">{template?.title()}</Text>
        </ModuleTitle>
        <ButtonLink
          href={{
            pathname: "/projects/create",
            query: {
              isTemplate: "true",
            },
          }}
          leftIcon={<Icon name="plus" />}
        >
          <Trans>New template</Trans>
        </ButtonLink>
      </HStack>
      <LoadingOverlay isLoading={store.fetcher.isLoading.value}>
        <DataTable>
          <DataTable.NavBar>
            <Box px="2" py="4">
              <FiltersPanel
                filters={store.where}
                searchPlaceholder={t`Search by brand, code, project`}
                searchDefaultValue={store.searchTerm}
                onSearch={store.handleSearch}
              />
            </Box>
          </DataTable.NavBar>
          <DataTable.Body
            tableKey={store.storeKey}
            columns={columns}
            data={store.projectTemplates}
            orderedBy={store.orderBy.clauses[0]}
            onOrderByChange={store.handleChangeOrderBy}
          />
          <Pagination store={store.pagination} />
        </DataTable>
      </LoadingOverlay>
      <ModalConfirm
        isOpen={store.isDeleteModalOpen}
        onClose={store.handleDeleteModalClose}
        onConfirm={store.handleDelete}
      >
        <Trans>Are you sure you want to delete this template?</Trans>
      </ModalConfirm>
    </Box>
  );
});
